<script setup>
  import { watch, nextTick } from "vue";
  import { storeToRefs } from "pinia";
  import { Info } from "lucide-vue-next";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useAiInsightConversationsStore } from "@stores/survey/assistant/aiInsightConversations";
  import { useAiInsightConversationMessagesStore } from "@stores/survey/assistant/aiInsightConversationMessages";
  import AiInsightMessageAnswerItem from "@/components/Survey/Assistant/AiInsightMessageAnswerItem.vue";
  import AiInsightMessageQuestionItem from "@/components/Survey/Assistant/AiInsightMessageQuestionItem.vue";

  const { isLive } = storeToRefs(useSurveyStore());
  const { selectedConversation } = storeToRefs(useAiInsightConversationsStore());

  const aiInsightConversationMessagesStore = useAiInsightConversationMessagesStore();
  const { conversationMessages, messagesContainerScrollTop } = storeToRefs(aiInsightConversationMessagesStore);

  const containerEl = () => document.getElementById("assistant-message-list__container");

  watch(
    () => conversationMessages.value?.at(-1),
    async () => {
      await nextTick();
      const container = containerEl();
      if (container) messagesContainerScrollTop.value = container.scrollHeight;
    },
    { deep: true },
  );

  watch(selectedConversation, async () => {
    await nextTick();
    const container = containerEl();
    if (container) container.scrollTo({ top: container.scrollHeight });
  });

  watch(messagesContainerScrollTop, async (newValue) => {
    await nextTick();
    const container = containerEl();
    if (!container) return;

    const { clientHeight, scrollTop, scrollHeight } = container;
    const buffer = clientHeight * 0.3;
    const isAtBottom = Math.ceil(clientHeight + scrollTop) >= scrollHeight - buffer;

    if (isAtBottom) container.scrollTo({ top: newValue });
  });
</script>

<template>
  <div
    id="assistant-message-list__container"
    class="min-h-0 flex-1 overflow-y-auto py-6">
    <div class="mx-auto flex max-w-[760px] flex-col gap-5 px-8">
      <!-- Partial-data notice for conversations generated while the survey is still live -->
      <div
        v-if="selectedConversation.is_created_before_complete && isLive"
        class="flex items-start gap-2 rounded-lg border bg-muted/50 px-3.5 py-2.5 text-13 text-muted-foreground">
        <Info class="mt-0.5 h-4 w-4 shrink-0" />
        <span>Your survey is not complete yet, so your insights might be generated out of partial data.</span>
      </div>

      <div
        v-for="(message, index) in conversationMessages"
        :id="`assistant-message-list__${index}-item`"
        :key="index"
        class="flex flex-col gap-4">
        <AiInsightMessageQuestionItem
          :index="index"
          :message="message"
          :is-auto-generated-summary="!!(selectedConversation.exec_summary || selectedConversation.objective_summary)" />
        <AiInsightMessageAnswerItem
          :index="index"
          :message="message" />
      </div>
    </div>
  </div>
</template>
